import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SAILLAB_LOGO_URL, getSystemImageUrl } from '../../../utils/media';

interface BookingErrorPageProps {
  /** Type of error - '404' for not found, 'locked' for feature-gated, 'error' for generic errors */
  type?: '404' | 'error' | 'locked';
  /**
   * ⚠️ THERE IS NO `errorMessage` PROP, DELIBERATELY.
   *
   * There was, and what reached it was `error.response.data.message ||
   * error.message` — an English sentence written for a log, or an axios one
   * like "Request failed with status code 500". A guest was shown whichever
   * turned up, on a page whose own copy is translated and friendly. The TYPE
   * of failure is all this page needs; every branch below says the rest in the
   * reader's language.
   *
   * If a failure ever needs its own wording, give it a `type` and a key —
   * never a string passed in from a catch block.
   */
  /**
   * WHY it is locked, when it is.
   *
   * 'not_released' means WE have not released the feature this page needs;
   * anything else means the host's plan does not include it. The two need
   * different words — the plan wording tells a guest their host must upgrade,
   * which is false and unfair when the cause is our own release schedule.
   */
  lockedReason?: string | null;
}

/**
 * BookingErrorPage Component
 *
 * Displays a friendly error page for booking-related errors.
 * Features a split layout with text on the left and sailboat image on the right,
 * with glitchy bilingual messaging that mixes English and Japanese.
 */
const BookingErrorPage: React.FC<BookingErrorPageProps> = ({
  type = '404',
  lockedReason
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const is404 = type === '404';
  const isLocked = type === 'locked';
  /** Locked by our release schedule rather than by the host's plan. */
  const isUnreleased = isLocked && lockedReason === 'not_released';

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen w-full flex flex-col">
      {/* Navigation bar */}
      <nav className="w-full bg-white border-b border-gray-200 px-4 py-3">
        <img
          src={SAILLAB_LOGO_URL}
          alt="SailLab"
          className="h-10 w-auto"
        />
      </nav>

      {/* Main content - Split layout */}
      <div className="flex-1 flex flex-col md:flex-row">
        {/* Left side - Text content with glitch container */}
        <div className="flex-1 flex items-center justify-center px-8 py-12 bg-white glitch-container">
          <div className="max-w-md text-center md:text-left">
            {/* Error Title - Glitchy text */}
            <h1 className="text-4xl sm:text-5xl font-bold text-primary-600 mb-6">
              {is404
                ? t('booking:errorPage.404.title', '4░4 - Pαgє N□† F⌐und')
                : isLocked
                  ? t('booking:errorPage.locked.title', 'Meeting Type Unavailable')
                  : t('booking:errorPage.error.title', 'Err□r - Syst░m Glitch')
              }
              {!isLocked && <span className="glitch-cursor">▌</span>}
            </h1>

            {/* Error Subtitle */}
            <p className="text-xl text-gray-600 mb-8">
              {is404
                ? t('booking:errorPage.404.subtitle', 'Thє pαgє you\'rє looкing for sєєms to hαvє driftєd into unchαrtєd wαtєrs...')
                : isUnreleased
                  ? t(
                      'booking:errorPage.locked.unavailableSubtitle',
                      'This booking page is not available at the moment. Please contact the host.'
                    )
                  : isLocked
                    ? t('booking:errorPage.locked.subtitle', 'The host needs to upgrade their plan to enable this meeting type.')
                    : t('booking:errorPage.error.subtitle', 'Somєthing glitchєd in thє systєm...')
              }
            </p>

            {/* Home Button */}
            <button
              onClick={handleGoHome}
              className="btn-primary"
            >
              {t('booking:errorPage.goHome', 'Bαck to Homєpαgє')}
            </button>
          </div>
        </div>

        {/* Right side - Image */}
        <div
          className="flex-1 min-h-[300px] md:min-h-0"
          style={{
            backgroundImage: `url(${getSystemImageUrl('404page-square.png')})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
      </div>
    </div>
  );
};

export default BookingErrorPage;
